import { useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import { useTheme } from '@/theme';
import { exitModalText, isHomePath } from '@/theme/vars';
import { useAppDispatch, useAppSelector } from '@/state/store';
import {
    setIsExitLocationModalOpen,
    openProfile,
    closeProfile,
    toggleInventory,
    toggleShowingSettingsMenu,
    toggleShopOpen,
    setIsLeaderboardOpen,
    setIsInformationOpen,
    toggleDebugMenu,
    setIsLocalePickerPanelVisible,
} from '@/state/features/app';
import { sendEvent } from '@/utils/analytics';
import { pathToURL } from '@/utils/urls';
import { isMobileWidth } from '@/utils/mobile';
import IconButton from '@/components/IconButton';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';

import * as styles from './styles.module.scss';

export default function FloatingHeaderBar({ classname }: { classname?: string }) {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const {
        isProfileOpen,
        isInventoryOpen,
        isShopOpen,
        isSettingsMenuShowing,
        isExitLocationModalOpen,
        hasSeenShop,
        isInformationOpen,
        isLeaderboardOpen,
        isLocalePickerPanelVisible,
    } = useAppSelector((state) => state.app);
    const [isExpanded, setIsExpanded] = useState(false);
    const { locationId } = useParams();

    // Some buttons are only available inside locations
    const isInLocation = locationId;
    const canShowExitModal = !isHomePath();
    const hasShopButton = isInLocation && (theme.metadata?.hasProfileShop || theme.metadata?.hasExternalShop);
    const hasInventoryButton = isInLocation && theme.metadata?.hasInventory;
    const shouldShowShopTooltips = theme.customJSON?.shouldShowShopTooltips;
    const hasInformationDrawer = theme.metadata?.customJSON?.informationDrawer?.enabled;
    const isLeftAligned = theme.customJSON?.floatingMenuIsLeftAligned; // Default is right aligned
    const hasLeaderboard = theme.customJSON?.leaderboard?.enabled;
    const hasProfileDrawer = theme.metadata?.customJSON?.profileDrawerV2?.enabled;
    const hasDebugMenu = theme.metadata?.customJSON?.menu?.hasDebugMenuButton;
    const logoImagePath = theme.metadata?.customJSON?.menu?.logoImagePath;
    const hasLocaleButton = process.env.PUBLIC_SUPPORT_TRANSLATIONS === 'true';

    return (
        <div
            className={classNames(styles.FloatingHeaderBar, classname, {
                [styles.isLeftAligned]: isLeftAligned,
                [styles.isExpanded]: isExpanded,
            })}
        >
            <button
                className={styles.expandButton}
                onClick={() => setIsExpanded(!isExpanded)}
                aria-label={isExpanded ? 'Collapse menu' : 'Expand menu'}
            >
                {logoImagePath ? (
                    <>
                        <img className={styles.logoIcon} src={pathToURL(logoImagePath)} alt="Logo Icon" />
                        <div className={styles.buttonStatus}>
                            <Icon type={isExpanded ? 'close' : 'menu'} size={12} />
                        </div>
                    </>
                ) : (
                    <Icon className={styles.noLogoIcon} type={isExpanded ? 'close' : 'menu'} size={30} />
                )}
                <Tooltip isVisible={shouldShowShopTooltips && hasShopButton && !hasSeenShop && isMobileWidth()}>
                    New Shop
                </Tooltip>
            </button>
            <div className={styles.buttonsContainer} inert={!isExpanded ? '' : undefined}>
                {hasInventoryButton && (
                    <IconButton
                        buttonStyle="round"
                        iconType="inventory"
                        title={isInventoryOpen ? 'Close Inventory' : 'Open Inventory'}
                        ariaLabel={isInventoryOpen ? 'Close Inventory' : 'Open Inventory'}
                        className={styles.button}
                        onClick={() => {
                            dispatch(toggleInventory());
                            dispatch(setIsExitLocationModalOpen(false));
                            dispatch(setIsLeaderboardOpen(false));
                            sendEvent(isInventoryOpen ? 'close_inventory' : 'open_inventory');
                        }}
                    />
                )}
                {hasProfileDrawer && isInLocation && (
                    <IconButton
                        buttonStyle="round"
                        iconType="profile"
                        title={isProfileOpen ? 'Close Profile' : 'Open Profile'}
                        ariaLabel={isProfileOpen ? 'Close Profile' : 'Open Profile'}
                        onClick={() => {
                            isProfileOpen ? dispatch(closeProfile()) : dispatch(openProfile());
                        }}
                        className={styles.button}
                    />
                )}
                {hasShopButton && (
                    <IconButton
                        buttonStyle="round"
                        iconType={theme.customJSON.shop?.shopButtonIcon ?? 'shop'}
                        title={isShopOpen ? 'Close Shop' : 'Open Shop'}
                        ariaLabel={isShopOpen ? 'Close Shop' : 'Open Shop'}
                        onClick={() => {
                            dispatch(toggleShopOpen());
                            isShopOpen ? sendEvent('close_shop_from_main_menu') : sendEvent('open_shop_from_main_menu');
                        }}
                        className={styles.button}
                        hasDot={shouldShowShopTooltips && !hasSeenShop && isExpanded}
                    />
                )}
                {canShowExitModal && (
                    <IconButton
                        buttonStyle="round"
                        iconType="world"
                        title={exitModalText}
                        ariaLabel={exitModalText}
                        className={styles.button}
                        onClick={() => {
                            if (!isExitLocationModalOpen) {
                                dispatch(setIsExitLocationModalOpen(true));
                                dispatch(setIsLeaderboardOpen(false));
                                sendEvent('open_exit_modal');
                            } else {
                                dispatch(setIsExitLocationModalOpen(false));
                            }
                        }}
                    />
                )}
                {/* <IconButton
                    buttonStyle="round"
                    iconType="settings"
                    title={isSettingsMenuShowing ? 'Close Settings' : 'Open Settings'}
                    ariaLabel={isSettingsMenuShowing ? 'Close Settings' : 'Open Settings'}
                    onClick={() => {
                        dispatch(toggleShowingSettingsMenu());
                        isSettingsMenuShowing ? '' : sendEvent('open_settings');
                    }}
                    className={styles.button}
                /> */}
                {hasInformationDrawer && (
                    <IconButton
                        buttonStyle="round"
                        iconType="info"
                        title={isInformationOpen ? 'Close Information' : 'Open Information'}
                        ariaLabel={isInformationOpen ? 'Close Information' : 'Open Information'}
                        onClick={() => {
                            dispatch(setIsInformationOpen(!isInformationOpen));
                            // isInformationOpen ? '' : sendEvent('open_information');
                        }}
                        className={styles.button}
                    />
                )}
                {hasLeaderboard && (
                    <IconButton
                        buttonStyle="round"
                        iconType="leaderboard"
                        title={isLeaderboardOpen ? 'Close Leaderboard' : 'Open Leaderboard'}
                        ariaLabel={isLeaderboardOpen ? 'Close Leaderboard' : 'Open Leaderboard'}
                        onClick={() => {
                            dispatch(setIsLeaderboardOpen(!isLeaderboardOpen));
                        }}
                        className={styles.button}
                    />
                )}
                {hasLocaleButton && (
                    <IconButton
                        buttonStyle="round"
                        iconType="translation"
                        title={isLocalePickerPanelVisible ? 'Close Language Picker' : 'Change Language'}
                        ariaLabel={isLocalePickerPanelVisible ? 'Close Language Picker' : 'Change Language'}
                        onClick={() => {
                            dispatch(setIsLocalePickerPanelVisible(!isLocalePickerPanelVisible));
                        }}
                        className={styles.button}
                    />
                )}
                {hasDebugMenu && (
                    <IconButton
                        ariaLabel="debug"
                        buttonStyle="round"
                        iconType="help"
                        onClick={() => {
                            dispatch(toggleDebugMenu());
                        }}
                        className={styles.button}
                    />
                )}
            </div>
        </div>
    );
}
